import React, { useMemo } from 'react';
import {
  UI_STATE_LABELS,
  UI_STATE_STYLES,
  formatIstClock,
} from '../../utils/closingMomentumWindow';

function StateStepper({ stateMachine, uiState }) {
  const steps = stateMachine?.steps || [];
  if (!steps.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {steps.map((step, idx) => {
        const isActive = step.status === 'active';
        const isComplete = step.status === 'complete';
        const chipClass = isActive
          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
          : isComplete
            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
            : 'bg-white text-gray-500 border-gray-200';
        return (
          <React.Fragment key={step.id}>
            <div
              className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${chipClass}`}
              title={step.description}
            >
              {step.label}
            </div>
            {idx < steps.length - 1 && (
              <span className={`text-xs ${isComplete ? 'text-emerald-500' : 'text-gray-300'}`}>→</span>
            )}
          </React.Fragment>
        );
      })}
      <span className={`ml-2 px-2 py-0.5 rounded border text-[10px] font-medium uppercase tracking-wide ${UI_STATE_STYLES[uiState] || UI_STATE_STYLES.idle}`}>
        {UI_STATE_LABELS[uiState] || uiState}
      </span>
    </div>
  );
}

function SqueezeBadge({ squeeze }) {
  if (!squeeze) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
        Squeeze data unavailable
      </div>
    );
  }

  const active = Boolean(squeeze.active);
  const width = typeof squeeze.bb_width_pct === 'number' ? squeeze.bb_width_pct : null;
  const threshold = typeof squeeze.threshold_pct === 'number' ? squeeze.threshold_pct : 1.2;

  return (
    <div
      className={`rounded-lg border p-3 ${
        active ? 'bg-violet-50 border-violet-200' : 'bg-white border-gray-200'
      }`}
    >
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">BB Squeeze</div>
      <div className={`mt-1 text-lg font-bold ${active ? 'text-violet-700' : 'text-gray-800'}`}>
        {width !== null ? `${width.toFixed(2)}%` : '—'}
      </div>
      <div className="mt-1 text-xs text-gray-600">
        {active ? 'Extreme squeeze' : 'Normal width'} · threshold &lt; {threshold.toFixed(2)}%
      </div>
      {width !== null && (
        <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`h-full rounded-full ${active ? 'bg-violet-500' : 'bg-sky-400'}`}
            style={{ width: `${Math.min(100, (width / Math.max(threshold * 2, 0.01)) * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function ScorePanel({ score }) {
  if (!score) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
        Score pending — waiting for live data
      </div>
    );
  }

  const leading = Number(score.leading_score || 0);
  const bullish = Number(score.bullish_score || 0);
  const bearish = Number(score.bearish_score || 0);
  const threshold = Number(score.directional_threshold || 70);
  const bias = score.directional_bias || 'Neutral';

  const biasClass =
    bias === 'Bullish'
      ? 'text-emerald-700'
      : bias === 'Bearish'
        ? 'text-red-700'
        : 'text-gray-700';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Live Score</div>
          <div className="mt-1 text-3xl font-bold text-gray-900">{leading.toFixed(1)}</div>
          <div className={`text-sm font-medium ${biasClass}`}>{bias} bias</div>
        </div>
        <div className="text-right text-xs text-gray-500">
          <div>Threshold {threshold}</div>
          {score.is_expiry_day && <div className="mt-1 text-amber-600 font-medium">Expiry day</div>}
          {score.would_trigger && <div className="mt-1 text-emerald-600 font-medium">Would trigger</div>}
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        <div className="flex items-center gap-2 text-xs">
          <span className="w-14 text-emerald-700 font-medium">Bull</span>
          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, bullish)}%` }} />
          </div>
          <span className="w-10 text-right text-gray-600">{bullish.toFixed(0)}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="w-14 text-red-700 font-medium">Bear</span>
          <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-red-500 rounded-full" style={{ width: `${Math.min(100, bearish)}%` }} />
          </div>
          <span className="w-10 text-right text-gray-600">{bearish.toFixed(0)}</span>
        </div>
      </div>

      {Array.isArray(score.factors) && score.factors.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Factor Breakdown</div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
            {score.factors.map((factor) => {
              const sideClass =
                factor.side === 'bullish'
                  ? 'text-emerald-700'
                  : factor.side === 'bearish'
                    ? 'text-red-700'
                    : 'text-gray-600';
              return (
                <div key={factor.key} className="flex items-start gap-2 text-xs">
                  <span className={`mt-0.5 ${factor.met ? sideClass : 'text-gray-400'}`}>
                    {factor.met ? '✓' : '○'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <span className="font-medium text-gray-800 truncate">{factor.label}</span>
                      <span className="text-gray-400 shrink-0">{factor.weight_pct}%</span>
                    </div>
                    {factor.details && (
                      <div className="text-gray-500 truncate" title={factor.details}>
                        {factor.details}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function OiMiniChart({ oiSeries }) {
  const series = useMemo(
    () => (Array.isArray(oiSeries) ? oiSeries : []),
    [oiSeries],
  );
  const chart = useMemo(() => {
    if (!series.length) return null;

    const pcrValues = series.map((p) => p.pcr).filter((v) => typeof v === 'number');
    const callDeltas = series.map((p) => p.call_oi_delta_pct).filter((v) => typeof v === 'number');
    const putDeltas = series.map((p) => p.put_oi_delta_pct).filter((v) => typeof v === 'number');

    const minPcr = pcrValues.length ? Math.min(...pcrValues) : 0;
    const maxPcr = pcrValues.length ? Math.max(...pcrValues) : 1;
    const pcrRange = Math.max(maxPcr - minPcr, 0.01);

    const width = 280;
    const height = 72;
    const padX = 4;
    const padY = 6;

    const pcrPoints = series
      .map((point, idx) => {
        if (typeof point.pcr !== 'number') return null;
        const x = padX + (idx / Math.max(series.length - 1, 1)) * (width - padX * 2);
        const y = height - padY - ((point.pcr - minPcr) / pcrRange) * (height - padY * 2);
        return `${x},${y}`;
      })
      .filter(Boolean)
      .join(' ');

    return { width, height, pcrPoints, callDeltas, putDeltas, minPcr, maxPcr };
  }, [series]);

  if (!series.length) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
        OI snapshots start at 2:45 PM IST
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">ΔOI / PCR</div>
        <div className="text-[10px] text-gray-400">{series.length} snapshots</div>
      </div>

      {chart?.pcrPoints && (
        <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className="mt-2 w-full h-16">
          <polyline
            fill="none"
            stroke="#6366f1"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={chart.pcrPoints}
          />
        </svg>
      )}

      <div className="mt-2 flex flex-wrap gap-1">
        {series.slice(-8).map((point) => (
          <div
            key={`${point.time}-${point.timestamp || ''}`}
            className="flex-1 min-w-[52px] rounded border border-gray-100 bg-gray-50 px-1 py-1 text-center"
          >
            <div className="text-[10px] text-gray-500">{point.time}</div>
            <div className="text-[10px] font-medium text-indigo-700">
              {typeof point.pcr === 'number' ? point.pcr.toFixed(2) : '—'}
            </div>
            <div className="text-[9px] text-emerald-600">
              C {typeof point.call_oi_delta_pct === 'number' ? `${point.call_oi_delta_pct > 0 ? '+' : ''}${point.call_oi_delta_pct.toFixed(1)}%` : '—'}
            </div>
            <div className="text-[9px] text-red-600">
              P {typeof point.put_oi_delta_pct === 'number' ? `${point.put_oi_delta_pct > 0 ? '+' : ''}${point.put_oi_delta_pct.toFixed(1)}%` : '—'}
            </div>
          </div>
        ))}
      </div>

      {chart && (
        <div className="mt-2 text-[10px] text-gray-400">
          PCR range {chart.minPcr.toFixed(2)} – {chart.maxPcr.toFixed(2)}
        </div>
      )}
    </div>
  );
}

function HeavyweightHeatmap({ heavyweights }) {
  const stocks = heavyweights?.stocks || [];
  if (!stocks.length) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
        Heavyweight quotes unavailable
      </div>
    );
  }

  const bias = heavyweights.bias || 'Neutral';
  const biasClass =
    bias === 'Bullish' ? 'text-emerald-700' : bias === 'Bearish' ? 'text-red-700' : 'text-gray-600';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Heavyweights</div>
        <div className={`text-xs font-medium ${biasClass}`}>
          {bias} · {heavyweights.green_count || 0}G / {heavyweights.red_count || 0}R
        </div>
      </div>
      <div className="mt-2 grid grid-cols-5 gap-1.5">
        {stocks.map((stock) => {
          const dir = stock.direction || 'flat';
          const cellClass =
            dir === 'green'
              ? 'bg-emerald-100 border-emerald-200 text-emerald-900'
              : dir === 'red'
                ? 'bg-red-100 border-red-200 text-red-900'
                : 'bg-gray-50 border-gray-200 text-gray-700';
          const change = typeof stock.change_pct === 'number' ? stock.change_pct : 0;
          return (
            <div
              key={stock.symbol}
              className={`rounded-md border px-1 py-2 text-center ${cellClass}`}
              title={`${stock.symbol}: ${change >= 0 ? '+' : ''}${change.toFixed(2)}%`}
            >
              <div className="text-[10px] font-bold truncate">{stock.symbol.replace('BANK', '')}</div>
              <div className="text-[10px] font-semibold mt-0.5">
                {change >= 0 ? '+' : ''}{change.toFixed(2)}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ClosingMomentumWindowPanel({ data, loading, error, onRefresh }) {
  if (!data?.active && !loading) {
    return null;
  }

  const uiState = data?.ui_state || 'gathering';
  const windowLabel = data?.window
    ? `${data.window.start}–${data.window.end} IST`
    : '2:45–3:00 PM IST';

  return (
    <div className="mb-4 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-indigo-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-indigo-900">3 PM Window</div>
          <div className="text-xs text-indigo-700/80">{windowLabel} · closing momentum</div>
        </div>
        <div className="flex items-center gap-2">
          {data?.timestamp && (
            <span className="text-[10px] text-gray-500">Updated {formatIstClock(data.timestamp)}</span>
          )}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="text-xs px-2 py-1 rounded border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        <StateStepper stateMachine={data?.state_machine} uiState={uiState} />

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1">{error}</div>
        )}

        {data?.observation_mode && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1">
            Observation mode — signals saved for review; auto-trade disabled. Hard exit by {data.hard_exit_by || '15:06'} IST.
          </div>
        )}

        {data?.strategy?.reasoning && (
          <div className="text-xs text-gray-700 bg-white/80 border border-gray-100 rounded px-2 py-1.5">
            {data.strategy.reasoning}
          </div>
        )}

        {data?.triggered_signal && (
          <div className="text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-2 py-1.5">
            Signal fired: {data.triggered_signal.signal_type} ({data.triggered_signal.strategy_type})
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <SqueezeBadge squeeze={data?.squeeze} />
          <ScorePanel score={data?.score} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <OiMiniChart oiSeries={data?.oi_series} />
          <HeavyweightHeatmap heavyweights={data?.heavyweights} />
        </div>
      </div>
    </div>
  );
}
